import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CloudUpload, ShieldCheck, AlertTriangle, History, RefreshCw, Download, X, CheckCircle2, XCircle } from "lucide-react";
import { API } from "../../config";
import { useITPortalI18n } from "../../i18n/ITPortalI18nContext";

const fmt = (tpl, vars) =>
  (tpl || "").replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));

// Card TelcaVoIP Cloud (pCloud) nel portale IT — stessa sorgente dati
// della card in Admin Dashboard (CR Fabrizio 08/07/2026: la card era
// visibile solo nell'Admin Dashboard, non nel portale IT).
export const TelcavoipCloudCard = () => {
  const { t, locale } = useITPortalI18n();
  const localeTag = locale === "IT" ? "it-IT" : "en-GB";
  const [status, setStatus] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [running, setRunning] = useState(false);
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/backup/telcavoip-cloud/status`);
      setStatus(res.data);
    } catch { /* card resta in stato loading-safe */ }
  }, []);

  const fetchRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const res = await axios.get(`${API}/backup/telcavoip-cloud/recent-runs?limit=10`);
      setRuns(res.data?.runs || []);
    } catch { setRuns([]); }
    finally { setRunsLoading(false); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  useEffect(() => {
    if (status?.enabled && status?.configured) fetchRuns();
  }, [status?.enabled, status?.configured, status?.last_upload_at, fetchRuns]);

  const downloadRestore = async () => {
    if (!restoreTarget?.fileid) return;
    setDownloading(true);
    try {
      const res = await axios.post(
        `${API}/backup/telcavoip-cloud/restore?fileid=${restoreTarget.fileid}&filename=${encodeURIComponent(restoreTarget.filename || "backup.json")}&source=it_portal`,
        null,
        {
          responseType: "blob",
          headers: { Authorization: `Bearer ${localStorage.getItem("it_token") || ""}` },
        }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url; a.download = restoreTarget.filename || "backup.json"; a.click();
      URL.revokeObjectURL(url);
      toast.success(fmt(t.tvc_restore_ok, { name: restoreTarget.filename }));
      setRestoreTarget(null);
    } catch {
      toast.error(t.tvc_restore_err);
    } finally { setDownloading(false); }
  };

  const toggle = async () => {
    setToggling(true);
    try {
      const next = !status?.enabled;
      const res = await axios.post(`${API}/backup/telcavoip-cloud/toggle?enabled=${next}`);
      if (res.data?.ok === false) {
        toast.error(res.data.message || t.tvc_toast_err);
      } else {
        toast.success(next ? t.tvc_toast_on : t.tvc_toast_off);
      }
      fetchStatus();
    } catch {
      toast.error(t.tvc_toast_err);
    } finally {
      setToggling(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/backup/telcavoip-cloud/run`);
      if (res.data?.ok === false) toast.error(res.data.error || t.tvc_toast_err);
      else toast.success(t.tvc_toast_run_ok);
      fetchStatus();
    } catch {
      toast.error(t.tvc_toast_err);
    } finally {
      setRunning(false);
    }
  };

  const gb = (bytes) => (bytes ? (bytes / (1024 ** 3)).toFixed(2) : "0.00");

  return (
    <div className="bg-gradient-to-br from-lojack-red/10 to-transparent border border-lojack-red/30 rounded-2xl p-6" data-testid="it-telcavoip-cloud-card">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-white font-medium flex items-center gap-2">
            <ShieldCheck size={16} className="text-lojack-red" /> {t.tvc_title}
          </p>
          <p className="text-gray-500 text-xs mt-1">{t.tvc_help}</p>
        </div>
        <button
          onClick={toggle}
          disabled={toggling || (!status?.configured && !status?.enabled)}
          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 ${status?.enabled ? "bg-lojack-red" : "bg-gray-700"} ${(toggling || (!status?.configured && !status?.enabled)) ? "opacity-50 cursor-not-allowed" : ""}`}
          data-testid="it-telcavoip-cloud-toggle"
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${status?.enabled ? "translate-x-6" : "translate-x-1"}`} />
        </button>
      </div>

      {status && !status.configured && (
        <p className="text-lojack-clause-300 text-xs mt-3 flex items-center gap-1.5">
          <AlertTriangle size={12} /> {t.tvc_not_configured}
        </p>
      )}

      {status?.enabled && (
        <div className="mt-4 space-y-2 text-xs">
          <p className="text-gray-400">
            {t.tvc_last_upload}:{" "}
            {status.last_upload_at ? (
              <span className="text-white">
                {new Date(status.last_upload_at).toLocaleString(localeTag)}
                {status.last_upload_size_kb && <span className="text-gray-500 ml-2">({status.last_upload_size_kb} KB)</span>}
              </span>
            ) : (
              <span className="text-gray-500 italic">{t.tvc_no_upload}</span>
            )}
          </p>
          {status.quota_bytes && (
            <p className="text-gray-400">
              {t.tvc_quota}: <span className="text-white">{gb(status.used_bytes)} GB</span>
              <span className="text-gray-500"> / {gb(status.quota_bytes)} GB</span>
            </p>
          )}
          {status.token_age_days !== null && status.token_age_days !== undefined && (
            <p className={status.token_expiring_soon ? "text-lojack-clause-300" : "text-gray-400"}>
              {t.tvc_token}: {fmt(t.tvc_token_age, { n: status.token_age_days })}
              {status.token_expiring_soon && (
                <span className="ml-2 font-semibold">
                  ⚠ {fmt(t.tvc_token_expiring, { n: status.token_expires_in_days })}
                </span>
              )}
            </p>
          )}
          {status.last_error && (
            <p className="text-lojack-clause-300">{t.tvc_last_error}: {status.last_error}</p>
          )}
          <button
            onClick={runNow}
            disabled={running}
            className="mt-2 flex items-center gap-2 px-4 py-2.5 bg-lojack-red/10 border border-lojack-red/40 text-white rounded-xl font-medium hover:bg-lojack-red/20 transition-colors disabled:opacity-50"
            data-testid="it-telcavoip-cloud-run-btn"
          >
            {running ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <CloudUpload size={16} />}
            {running ? t.tvc_running : t.tvc_run_now}
          </button>

          {/* Cronologia ultimi 10 upload con esito */}
          <div className="mt-4 bg-black/30 border border-gray-800 rounded-xl p-3" data-testid="it-telcavoip-cloud-history">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 flex items-center gap-1.5">
                <History size={12} /> {t.tvc_history_title}
              </span>
              <button onClick={fetchRuns} disabled={runsLoading}
                className="text-gray-500 hover:text-white transition-colors"
                data-testid="it-telcavoip-cloud-history-refresh">
                <RefreshCw size={12} className={runsLoading ? "animate-spin" : ""} />
              </button>
            </div>
            {runs.length > 0 ? (
              <div className="space-y-1.5 max-h-56 overflow-y-auto">
                {runs.map((r, i) => (
                  <div key={i} className="flex items-center justify-between bg-black/40 rounded-lg px-3 py-2 gap-2" data-testid={`it-telcavoip-run-${i}`}>
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {r.ok
                        ? <CheckCircle2 size={13} className="text-emerald-400 flex-shrink-0" />
                        : <XCircle size={13} className="text-lojack-red flex-shrink-0" />}
                      <div className="min-w-0">
                        <p className="text-white truncate" title={r.filename || r.error}>
                          {r.filename || r.error || "—"}
                        </p>
                        <p className="text-gray-500 text-[10px]">
                          {r.created_at ? new Date(r.created_at).toLocaleString(localeTag, { dateStyle: "short", timeStyle: "short" }) : "—"}
                          {r.size_kb ? ` · ${r.size_kb} KB` : ""}
                          <span className={`ml-1.5 font-semibold ${r.ok ? "text-emerald-400" : "text-lojack-red"}`}>
                            {r.ok ? t.tvc_ok : t.tvc_ko}
                          </span>
                        </p>
                      </div>
                    </div>
                    {r.ok && r.fileid && (
                      <button
                        onClick={() => setRestoreTarget(r)}
                        className="flex-shrink-0 px-2.5 py-1 bg-white/5 border border-gray-700 hover:border-lojack-red/50 hover:bg-lojack-red/10 text-gray-300 rounded-lg text-[10px] font-medium flex items-center gap-1 transition-colors"
                        data-testid={`it-telcavoip-restore-${i}`}
                      >
                        <Download size={10} /> {t.tvc_restore}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 text-center py-3">{runsLoading ? "..." : t.tvc_history_empty}</p>
            )}
          </div>
        </div>
      )}

      {/* Modale restore guidato */}
      {restoreTarget && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => !downloading && setRestoreTarget(null)} data-testid="it-telcavoip-restore-modal">
          <div className="bg-[#0a0a0a] border border-gray-800 rounded-2xl p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Download size={16} className="text-lojack-red" /> {t.tvc_restore_title}
              </h3>
              <button onClick={() => setRestoreTarget(null)} disabled={downloading} className="text-gray-500 hover:text-white" data-testid="it-telcavoip-restore-close">
                <X size={16} />
              </button>
            </div>
            <p className="text-white text-sm font-mono mb-3 break-all">{restoreTarget.filename}</p>
            <p className="text-gray-400 text-xs mb-2">{t.tvc_restore_desc}</p>
            <ol className="text-xs text-gray-400 list-decimal pl-4 mb-3 space-y-1">
              <li>{t.tvc_restore_step1}</li>
              <li>{t.tvc_restore_step2}</li>
              <li>{t.tvc_restore_step3}</li>
            </ol>
            <p className="text-[10px] text-lojack-clause-300 mb-2 flex items-start gap-1.5">
              <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" /> {t.tvc_restore_warn}
            </p>
            <p className="text-[10px] text-gray-500 mb-4" data-testid="it-telcavoip-restore-audit-note">
              {t.tvc_restore_audit_note}
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setRestoreTarget(null)} disabled={downloading}
                className="px-3 py-2 text-gray-400 hover:text-white text-sm transition-colors" data-testid="it-telcavoip-restore-cancel">
                {t.tvc_restore_cancel}
              </button>
              <button onClick={downloadRestore} disabled={downloading}
                className="px-4 py-2 bg-lojack-red hover:bg-lojack-red/80 disabled:opacity-50 text-white rounded-xl text-sm font-medium flex items-center gap-2 transition-colors"
                data-testid="it-telcavoip-restore-download">
                {downloading ? <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Download size={14} />}
                {downloading ? t.tvc_restore_downloading : t.tvc_restore_download}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TelcavoipCloudCard;
